import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { OutputLanguage } from "@alchema/shared";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import { CoverLetterEditor } from "~/components/CoverLetterEditor";
import { exportCoverLetterPdf, exportCoverLetterDocx } from "~/lib/export";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/cover-letters/$coverId")({
  beforeLoad: async () => { await requireAuth(); },
  component: CoverLetterDetailPage,
  pendingComponent: () => (
    <div className="animate-fade-up">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="mt-4 h-[400px] rounded-xl" />
    </div>
  ),
});

function CoverLetterDetailPage() {
  const { coverId } = Route.useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const coverLetterQuery = useQuery(
    orpc.coverLetter.get.queryOptions({ input: { id: coverId } }),
  );

  const updateMutation = useMutation(orpc.coverLetter.update.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.get.key({ input: { id: coverId } }) });
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.list.key({}) });
    },
  }));

  const regenerateMutation = useMutation(orpc.coverLetter.regenerate.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.get.key({ input: { id: coverId } }) });
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.list.key({}) });
      toast(m.cover_letters_detail_toast_regenerated(), "success");
    },
    onError: (err) => {
      toast(err.message || m.cover_letters_detail_toast_regenerate_error(), "error");
    },
  }));

  const cl = coverLetterQuery.data;

  if (coverLetterQuery.isLoading) {
    return (
      <div className="animate-fade-up">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-[400px] rounded-xl" />
      </div>
    );
  }

  if (!cl) {
    return (
      <div className="card mt-8 flex flex-col items-center justify-center py-16 text-center animate-fade-up">
        <p className="text-sm font-medium text-[var(--text-secondary)]">{m.cover_letters_detail_not_found()}</p>
        <button
          onClick={() => navigate({ to: "/cover-letters" })}
          className="btn btn-secondary mt-4"
        >
          {m.cover_letters_detail_back()}
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      {/* Toolbar card */}
      <div className="card animate-fade-up stagger-1">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate({ to: "/cover-letters" })}
            className="btn btn-ghost btn-sm flex items-center gap-1.5"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            {m.common_back()}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportCoverLetterPdf(cl.content, cl.title)}
              className="btn btn-secondary btn-sm"
              data-testid="cover-letter-export-pdf"
            >
              {m.cover_letters_detail_export_pdf()}
            </button>
            <button
              onClick={() => exportCoverLetterDocx(cl.content, cl.title)}
              className="btn btn-secondary btn-sm"
              data-testid="cover-letter-export-docx"
            >
              {m.cover_letters_detail_export_docx()}
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="mt-4 animate-fade-up stagger-2">
        <CoverLetterEditor
          coverLetter={cl}
          onSave={(content) => {
            updateMutation.mutate({ id: coverId, content });
          }}
          onTitleChange={(title) => {
            updateMutation.mutate({ id: coverId, title });
          }}
          onToneChange={(tone) => {
            updateMutation.mutate({ id: coverId, tone: tone as "formal" | "conversational" | "enthusiastic" });
          }}
          onRegenerate={(tone, outputLanguage) => {
            regenerateMutation.mutate({
              id: coverId,
              tone: tone as "formal" | "conversational" | "enthusiastic",
              outputLanguage: outputLanguage as OutputLanguage | undefined,
            });
          }}
          detectedLanguage={(cl?.parsedRequirements as Record<string, unknown> | null)?.detectedLanguage as string | undefined}
          isRegenerating={regenerateMutation.isPending}
        />
      </div>
    </div>
  );
}
